import { useEffect, useState } from 'react'

import {
  useEventsOnLoadQuery,
  useEventsScrollCursorLazyQuery,
} from '@/graphql/generated/hooks'
import { startTimer, trackEvent } from '@/utils/analytics'

import { EventFiltersI, EventsEdgeT } from '../types/traceLogTypes'
import {
  DEVICES_TAB_EVENT,
  FACILITIES_TAB_EVENT,
  SCROLL_DOWN_EVENT,
  SCROLL_UP_EVENT,
  VIEW_TL_TAB_EVENT,
  getMixpanelEventData,
} from '../utils/traceLogUtils'

interface UseEventsParamsI extends EventFiltersI {
  centerTimestamp?: string
}

const EVENT_FETCH_COUNT = 25

export const useEvents = ({
  incident,
  filters,
  centerTimestamp,
}: UseEventsParamsI) => {
  // Use the highlighted event timestamp if provided, otherwise fall back to incident detectedAt
  const mainTimestamp = centerTimestamp || incident?.detectedAt
  const [eventCursor, setEventCursor] = useState({
    scrollUp: null,
    scrollDown: null,
  })
  const [hasOlder, setHasOlder] = useState(false)
  const [events, setEvents] = useState<EventsEdgeT[]>([])
  const [isLoading, setIsLoading] = useState(true)

  const eventName = filters?.deviceId ? DEVICES_TAB_EVENT : FACILITIES_TAB_EVENT
  const tabEventName = `${eventName} ${VIEW_TL_TAB_EVENT}`
  const mixpanelData = getMixpanelEventData(incident)

  useEventsOnLoadQuery({
    skip: !incident || !mainTimestamp,
    errorPolicy: 'all',
    variables: {
      facilityId: filters?.facilityId,
      deviceId: filters?.deviceId,
      mainEventTimestamp: mainTimestamp,
      firstANDLastCount: EVENT_FETCH_COUNT,
    },
    onCompleted: (data) => {
      // On initial load set start/end cursor
      if (eventCursor.scrollUp === null) {
        setEventCursor({
          ...eventCursor,
          // The cursor to load the more recent events (on scroll up)
          scrollUp: data?.eventsAfter?.pageInfo?.startCursor,
          // The cursor to load the older events (on scroll down)
          scrollDown: data?.eventsBefore?.pageInfo?.endCursor,
        })
        setEvents([...data?.eventsAfter?.edges, ...data?.eventsBefore?.edges])
        setHasOlder(Boolean(data?.eventsBefore?.pageInfo?.endCursor))
        setIsLoading(false)

        trackEvent(tabEventName, mixpanelData)
      }
    },
  })

  const [getMoreEvents] = useEventsScrollCursorLazyQuery({
    errorPolicy: 'all',
  })

  const getNewerEvents = async () => {
    setIsLoading(true)
    startTimer(SCROLL_UP_EVENT)
    const { data: eventData } = await getMoreEvents({
      fetchPolicy: 'cache-and-network',
      variables: {
        last: EVENT_FETCH_COUNT,
        before: eventCursor?.scrollUp,
        facilityId: filters.facilityId,
        deviceId: filters.deviceId,
        detectedAtGte: mainTimestamp,
      },
    })
    if (eventData?.eventPresentations?.pageInfo?.startCursor) {
      setEventCursor({
        ...eventCursor,
        scrollUp: eventData?.eventPresentations?.pageInfo?.startCursor,
      })
    }

    trackEvent(SCROLL_UP_EVENT, mixpanelData)
    setEvents([...eventData?.eventPresentations?.edges, ...events])
    setIsLoading(false)
    return Boolean(eventData?.eventPresentations?.pageInfo?.startCursor)
  }

  const getOlderEvents = async () => {
    setIsLoading(true)
    startTimer(SCROLL_DOWN_EVENT)
    const { data: eventData } = await getMoreEvents({
      variables: {
        first: EVENT_FETCH_COUNT,
        after: eventCursor?.scrollDown,
        facilityId: filters.facilityId,
        deviceId: filters.deviceId,
        detectedAtLt: mainTimestamp,
      },
    })
    setEventCursor({
      ...eventCursor,
      scrollDown: eventData?.eventPresentations?.pageInfo?.endCursor,
    })

    trackEvent(SCROLL_DOWN_EVENT, mixpanelData)
    setHasOlder(Boolean(eventData?.eventPresentations?.pageInfo?.endCursor))
    setEvents([...events, ...eventData?.eventPresentations?.edges])
    setIsLoading(false)
  }

  useEffect(() => {
    startTimer(tabEventName)
  }, [])

  return {
    isLoading,
    events,
    hasOlder,
    getNewerEvents,
    getOlderEvents,
  }
}
